import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useStoreState } from "easy-peasy";

import { Button, Modal, Typography } from "stories/components";
import { Input } from "stories/forms";
import { useNotification } from "stories/components/Notification";

import {
  httpGetPortfolioSecuritiesList,
  httpTransferSecurity,
} from "services/portfolios";

const STAGE_SELECT = "select";
const STAGE_CONFIRM = "confirm";

const securityKey = (securityType, securityId) =>
  securityType && securityId != null ? `${securityType}:${securityId}` : "";

const splitKey = (key) => {
  if (!key) return { securityType: "", securityId: null };
  const [securityType, securityIdRaw] = key.split(":");
  const securityId = Number(securityIdRaw);
  return { securityType, securityId: Number.isFinite(securityId) ? securityId : null };
};

const TransferSecurityModal = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { notify } = useNotification();

  const sourcePortfolio = useStoreState((state) => state.portfolio?.portfolio);
  const portfolios = useStoreState((state) => state.portfolio?.portfolios);

  const [stage, setStage] = useState(STAGE_SELECT);
  const [securities, setSecurities] = useState([]);
  const [loadingSecurities, setLoadingSecurities] = useState(false);
  const [selectedSecurityKey, setSelectedSecurityKey] = useState("");
  const [selectedTargetId, setSelectedTargetId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reset all state every time the modal is reopened.
  useEffect(() => {
    if (!isOpen) return;
    setStage(STAGE_SELECT);
    setSelectedSecurityKey("");
    setSelectedTargetId("");
    setSubmitting(false);
  }, [isOpen]);

  // Fetch securities for the source portfolio when the modal opens.
  useEffect(() => {
    if (!isOpen || !sourcePortfolio?.id) return;
    let cancelled = false;
    setLoadingSecurities(true);
    httpGetPortfolioSecuritiesList(sourcePortfolio.id)
      .then((res) => {
        if (cancelled) return;
        setSecurities(res?.data?.securities || []);
      })
      .catch(() => {
        if (cancelled) return;
        setSecurities([]);
        notify({
          title: t(
            "transfer_security_error_loading_securities",
            "Arvopaperien lataus epäonnistui"
          ),
          type: "danger",
        });
      })
      .finally(() => {
        if (!cancelled) setLoadingSecurities(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, sourcePortfolio?.id, notify, t]);

  const sameLicensePortfolios = useMemo(() => {
    if (!Array.isArray(portfolios) || !sourcePortfolio?.account) return [];
    return portfolios.filter((p) => p?.account === sourcePortfolio.account);
  }, [portfolios, sourcePortfolio?.account]);

  const selectedSecurity = useMemo(() => {
    const { securityType, securityId } = splitKey(selectedSecurityKey);
    return securities.find(
      (s) => s.security_type === securityType && s.security_id === securityId
    );
  }, [securities, selectedSecurityKey]);

  const selectedTargetPortfolio = useMemo(() => {
    if (!selectedTargetId) return null;
    return sameLicensePortfolios.find((p) => String(p.id) === String(selectedTargetId));
  }, [sameLicensePortfolios, selectedTargetId]);

  const canProceed =
    !!selectedSecurity && !!selectedTargetPortfolio && !submitting;

  const handleProceedToConfirm = () => {
    if (!canProceed) return;
    setStage(STAGE_CONFIRM);
  };

  const handleBackToSelect = () => {
    setStage(STAGE_SELECT);
  };

  const handleConfirmTransfer = () => {
    if (!sourcePortfolio?.id || !selectedSecurity || !selectedTargetPortfolio) return;
    setSubmitting(true);
    httpTransferSecurity({
      sourcePortfolioId: sourcePortfolio.id,
      targetPortfolioId: selectedTargetPortfolio.id,
      securityType: selectedSecurity.security_type,
      securityId: selectedSecurity.security_id,
    })
      .then((res) => {
        const moved = res?.data?.moved || {};
        const totalMoved =
          (moved.transactions || 0) +
          (moved.pre_transactions || 0) +
          (moved.options || 0) +
          (moved.pre_options || 0);
        notify({
          title: t("transfer_security_success", "Arvopaperi siirretty onnistuneesti"),
          message: t(
            "transfer_security_success_count",
            "Siirrettyjä rivejä: {{count}}",
            { count: totalMoved }
          ),
          type: "success",
        });
        if (typeof onSuccess === "function") {
          onSuccess(res?.data);
        }
        onClose();
      })
      .catch((error) => {
        const detail =
          error?.data?.detail ||
          t(
            "transfer_security_error",
            "Siirto epäonnistui, mitään ei muutettu"
          );
        notify({
          title: t("transfer_security_error_title", "Siirto epäonnistui"),
          message: detail,
          type: "danger",
        });
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const renderSelectStage = () => (
    <>
      <Typography variant="h3" className="mb-3">
        {t("transfer_security_title", "Siirrä arvopaperi salkusta toiseen")}
      </Typography>

      {sourcePortfolio?.name && (
        <Typography variant="small" className="mb-3 d-block">
          {t("transfer_security_source_portfolio", "Lähdesalkku")}:{" "}
          <strong>{sourcePortfolio.name}</strong>
        </Typography>
      )}

      <div className="mb-3">
        <Input
          label={t("transfer_security_select_security", "Valitse arvopaperi")}
          type="select"
          name="security"
          value={selectedSecurityKey}
          onChange={(e) => setSelectedSecurityKey(e.target.value)}
          disabled={loadingSecurities || submitting}
        >
          <option value="">
            {loadingSecurities
              ? t("loading", "Ladataan...")
              : t("transfer_security_select_security_placeholder", "— Valitse —")}
          </option>
          {securities.map((s) => {
            const key = securityKey(s.security_type, s.security_id);
            const label =
              s.name && s.ticker_symbol
                ? `${s.name} (${s.ticker_symbol})`
                : s.name || s.ticker_symbol || `#${s.security_id}`;
            return (
              <option key={key} value={key}>
                {label}
              </option>
            );
          })}
        </Input>
        {!loadingSecurities && securities.length === 0 && (
          <Typography variant="small" className="text-muted">
            {t(
              "transfer_security_no_securities",
              "Lähdesalkussa ei ole siirrettäviä arvopapereita."
            )}
          </Typography>
        )}
      </div>

      <div className="mb-3">
        <Input
          label={t("transfer_security_select_target", "Valitse kohdesalkku")}
          type="select"
          name="target_portfolio"
          value={selectedTargetId}
          onChange={(e) => setSelectedTargetId(e.target.value)}
          disabled={submitting}
        >
          <option value="">
            {t("transfer_security_select_target_placeholder", "— Valitse —")}
          </option>
          {sameLicensePortfolios.map((p) => (
            <option
              key={p.id}
              value={p.id}
              disabled={p.id === sourcePortfolio?.id}
            >
              {p.id === sourcePortfolio?.id
                ? `${p.name} (${t(
                    "transfer_security_active_portfolio",
                    "aktiivinen salkku"
                  )})`
                : p.name}
            </option>
          ))}
        </Input>
      </div>

      <div className="d-flex justify-content-between mt-4">
        <Button color="secondary" onClick={onClose} disabled={submitting}>
          {t("cancel", "Peruuta")}
        </Button>
        <Button
          color="primary"
          onClick={handleProceedToConfirm}
          disabled={!canProceed}
        >
          {t("transfer", "Siirrä")}
        </Button>
      </div>
    </>
  );

  const renderConfirmStage = () => (
    <>
      <Typography variant="h3" className="mb-3">
        {t("transfer_security_confirm_title", "Vahvista siirto")}
      </Typography>
      <Typography variant="p" className="mb-3 d-block">
        {t(
          "transfer_security_confirm_body",
          'Siirretään arvopaperi "{{security}}" salkusta "{{source}}" salkkuun "{{target}}". Kaikki transaktiot, optiot ja keskeneräiset käsittelyrivit siirtyvät yhdellä atomisella operaatiolla. Toimintoa ei voi peruuttaa muuten kuin tekemällä siirto käänteisesti.',
          {
            security: selectedSecurity
              ? selectedSecurity.name && selectedSecurity.ticker_symbol
                ? `${selectedSecurity.name} (${selectedSecurity.ticker_symbol})`
                : selectedSecurity.name ||
                  selectedSecurity.ticker_symbol ||
                  `#${selectedSecurity.security_id}`
              : "",
            source: sourcePortfolio?.name || "",
            target: selectedTargetPortfolio?.name || "",
          }
        )}
      </Typography>

      <div className="d-flex justify-content-between mt-4">
        <Button color="secondary" onClick={handleBackToSelect} disabled={submitting}>
          {t("cancel", "Peruuta")}
        </Button>
        <Button
          color="danger"
          onClick={handleConfirmTransfer}
          disabled={submitting}
        >
          {submitting
            ? t("transfer_security_submitting", "Siirretään...")
            : t("confirm_transfer", "Vahvista siirto")}
        </Button>
      </div>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      toggleModal={submitting ? undefined : onClose}
      size="modal-lg"
      showHeader={false}
    >
      <div className="p-3">
        {stage === STAGE_SELECT ? renderSelectStage() : renderConfirmStage()}
      </div>
    </Modal>
  );
};

export default TransferSecurityModal;
